import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { T, TElement } from '../../hooks/useTranslate';
import AccessTimeFilledIcon from '@mui/icons-material/AccessTimeFilled';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import {
  iconSizingStyle,
  gridTextStyle
} from './drawer-styles';

interface ProfileTimezoneLineProps {
  customerHrefData: {
    timezone?: string;
    humanName?: {
      firstName?: string;
      lastName?: string;
    };
  };
  drawerType: string;
}

export function ProfileTimezoneLine({ customerHrefData, drawerType }: ProfileTimezoneLineProps) {
  dayjs.extend(utc);
  dayjs.extend(timezone);

  let currentTime: string | null = null;
  if (customerHrefData.timezone) {
    try {
      currentTime = dayjs().tz(customerHrefData.timezone).format('h:mm A');
    } catch {
      // Some profiles (confirmed in QA test data) have a timezone value
      // that isn't a real IANA zone (e.g. "America/Seattle" -- Seattle is
      // actually America/Los_Angeles). dayjs's timezone plugin defers to
      // native Intl/Date.toLocaleString, which throws a RangeError for an
      // unrecognized zone instead of degrading gracefully like the
      // previous moment-timezone implementation did. Treat it the same as
      // "no timezone on file" rather than crashing the whole profile
      // drawer. See GO-3190.
      currentTime = null;
    }
  }

  const customerName = `${customerHrefData.humanName?.firstName} ${customerHrefData.humanName?.lastName}`;

  return (
    <Stack direction="row" gap={1}>
      <AccessTimeFilledIcon sx={iconSizingStyle} />
      {currentTime ? (
        <Typography
          sx={{
            ...gridTextStyle,
            marginLeft: 1.2
          }}
          data-testid={`${drawerType}-profile-timezone`}
        >
          <TElement
            isUpperCase={false}
            isTitleCase={false}
            param0={<Box component="span" sx={{ fontWeight: 600 }}>{currentTime}</Box>}
            param1={customerName}
          >
            its_time_for
          </TElement>
        </Typography>
      ) : (
        <Typography
          sx={gridTextStyle}
          data-testid={`${drawerType}-profile-timezone`}
        >
          <T isUpperCase={false} isTitleCase={false}>not_available</T>
        </Typography>
      )}
    </Stack>
  );
}